import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import { Periods } from '@app/src/components/CreateNewRequestModal/PeriodSelector/PeriodSelector'
import { PROVIDER_SELECTOR_ALLOWED_FILTERS } from '@app/src/components/CreateNewRequestModal/ProviderSelectorFilterDrawer'
import { calculateStartsAndEndsAtDate } from '@app/src/components/CreateNewRequestModal/utils'
import CreationModalContainer from '@app/src/components/CreationModal/CreationModalContainer'
import CreationModalStep from '@app/src/components/CreationModal/CreationModalStep'
import { QuestionnaireType, useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import { useQueryState } from '@app/src/hooks/queryState'
import type { AutomationCardItem } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/AutomationsConfigLogic'
import AutomationCategories from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreationSteps/AutomationCategories'
import AutomationDeadlineOffset from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreationSteps/AutomationDeadlineOffset'
import AutomationPeriod from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreationSteps/AutomationPeriod'
import AutomationQuestionnaires from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreationSteps/AutomationQuestionaires'
import AutomationQuestionnaireType from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreationSteps/AutomationQuestionaireType'
import AutomationSummary from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreationSteps/AutomationSummary'
import AutomationType from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreationSteps/AutomationType'
import useAutomationAffectedCount from '@app/src/pages/Configurations/ConfigurationsPages/Automation/Hooks/useAutomationAffectedCount'
import { RequestAutomationType } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/RequestAutomationType'
import SelectTemplateForScraping from '@app/src/pages/ResourceCollection/Collections/Provider/ScheduleScreening/SelectTemplateForScraping'
import Filters from '@app/src/pages/ResourceCollection/Filters/Filters'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { RequestAutomation } from '@app/src/types/automations'
import type { QuestionnaireTemplate } from '@app/src/types/resourceExplorer'
import { getPeriodOrderNumber, getPeriodType } from '@app/src/utils'
import { getDateWithoutTimeFromString, insertIf } from '@app/src/utils/helpersTs'
import { NotificationSeverity } from '@app/src/wf-constants'
import { Dialog, Skeleton } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { validateAutomationAssessmentFilters } from '../../../../utils/automationValidation'
import { buildAutomationPayload, requiresApplyToExistingSelection } from './automationUtils'
import AutomationAssessments from './CreationSteps/AutomationAssessments'

interface CreateAutomationModalProps {
  onClose: () => void
  open: boolean
  editedAutomation?: AutomationCardItem
  selectedQuestionnaireTemplates?: QuestionnaireTemplate[]
  onSaved?: () => void | Promise<void>
  onSelectExpectation?: () => void
}

export enum AUTOMATION_QUESTION_KEYS {
  AUTOMATION_TYPE = 'automationType',
  CATEGORY_FILTERS = 'categoryFilters',
  REPORT_TYPE = 'questionnaireType',
  TEMPLATES = 'templates',
  DATE_TYPE = 'dateType',
  DATE_YEAR = 'dateYear',
  DATE_PERIOD = 'datePeriod',
  DEADLINE_OFFSET = 'deadlineOffsetDays',
  DEADLINE_OFFSET_TYPE = 'deadlineOffsetType',
  CUSTOM_DUE_DATE = 'customDueDate',
  APPLY_TO_EXISTING = 'applyToExisting',
  REVIEW = 'review',
}

export interface AutomationFormData {
  id?: number
  questionnaireType: QuestionnaireType
  automationType: RequestAutomationType
  templates: Array<{ id: number; title: string }>
  dateType: Periods
  categoryFilters?: FilterGroup[]
  dateYear: string | null
  datePeriod: number | null
  deadlineOffsetDays?: number
  deadlineOffsetType?: number | 'custom'
  customDueDate?: Date | string | null
  applyToExisting?: boolean
}

interface AutomationQuestion {
  title: string
  description?: React.ReactNode
  fieldName: AUTOMATION_QUESTION_KEYS | '' | Array<AUTOMATION_QUESTION_KEYS | ''>
  children: JSX.Element
  overrideButtonTextId?: string | undefined
  hideNextButton?: boolean
  hasSkipButton?: boolean
  disableInitialBack?: boolean
  canGoNext?: (values: unknown) => boolean
  customOnClickSkip?: () => void
  footerContent?: React.ReactNode
}

const mapToFormData = (requestAutomation: RequestAutomation): AutomationFormData => {
  const periodType = getPeriodType(requestAutomation.periodStartsAt, requestAutomation.periodEndsAt)
  const isStandardReport = requestAutomation.templateAutomations?.[0]?.template.isStandardReport

  return {
    id: requestAutomation.id,
    questionnaireType: isStandardReport ? QuestionnaireType.WF : QuestionnaireType.COMPANY,
    automationType: requestAutomation.automationType,
    categoryFilters: requestAutomation.categoryFilters,
    templates: requestAutomation.templateAutomations?.map(t => ({ id: t.template.id, title: t.template.title })) ?? [],
    deadlineOffsetDays: requestAutomation.deadlineOffSetDays,
    deadlineOffsetType: requestAutomation.customDueDate ? 'custom' : requestAutomation.deadlineOffSetDays,
    customDueDate: requestAutomation.customDueDate
      ? getDateWithoutTimeFromString(requestAutomation.customDueDate)
      : undefined,
    dateType: periodType,
    dateYear: requestAutomation.periodStartsAt
      ? getDateWithoutTimeFromString(requestAutomation.periodStartsAt).getFullYear().toString()
      : null,
    datePeriod: requestAutomation.periodStartsAt
      ? getPeriodOrderNumber(requestAutomation.periodStartsAt, periodType)
      : null,
    applyToExisting:
      requestAutomation.automationType === RequestAutomationType.AI_ENRICH ||
      requestAutomation.automationType === RequestAutomationType.AI_SCREEN ||
      requestAutomation.automationType === RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY ||
      requestAutomation.automationType === RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL
        ? false
        : undefined,
  }
}

const CreateAutomationModal: React.FC<CreateAutomationModalProps> = ({
  onClose,
  open,
  editedAutomation,
  selectedQuestionnaireTemplates,
  onSaved,
  onSelectExpectation,
}) => {
  const { activeStep, setActiveStep, setTotalSteps } = useCreationModalProgress()
  const { formatMessage } = useIntl()
  const { mutateAsync: createAutomation, isPending: isAutomationLoading } = useUpdateResource<
    RequestAutomation,
    unknown
  >()
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const [, setQueryFilters] = useQueryState('filters', [])

  const editedRequestAutomation = editedAutomation?.requestAutomation

  const formMethods = useForm<AutomationFormData>({
    shouldUnregister: false,
    defaultValues: {
      [AUTOMATION_QUESTION_KEYS.TEMPLATES]: [],
      [AUTOMATION_QUESTION_KEYS.DATE_TYPE]: Periods.Yearly,
      [AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS]: [],
    },
  })

  const { watch, handleSubmit, reset, setValue, setError, formState } = formMethods

  useEffect(() => {
    if (selectedQuestionnaireTemplates?.length) {
      setValue(
        AUTOMATION_QUESTION_KEYS.TEMPLATES,
        selectedQuestionnaireTemplates?.map(x => ({ id: x.id, title: x.title })) ?? [],
      )
      setValue(AUTOMATION_QUESTION_KEYS.REPORT_TYPE, QuestionnaireType.WF)
    }
  }, [selectedQuestionnaireTemplates])

  useEffect(() => {
    if (!editedRequestAutomation) {
      reset({})
      return
    }

    const automationFormData = mapToFormData(editedRequestAutomation)
    reset(automationFormData)
  }, [editedAutomation?.id, editedAutomation?.type])

  const questionnaireType = watch(AUTOMATION_QUESTION_KEYS.REPORT_TYPE)
  const requestAutomationType = watch(AUTOMATION_QUESTION_KEYS.AUTOMATION_TYPE)

  useEffect(() => {
    if (!open) return
    if (editedAutomation) return
    if (requestAutomationType === RequestAutomationType.EXPECTATION && onSelectExpectation) {
      onSelectExpectation()
    }
  }, [requestAutomationType, open, editedAutomation, onSelectExpectation])

  const { affectedCount, isLoadingAffectedCount } = useAutomationAffectedCount(formMethods)

  const getReviewStepDescription = (): React.ReactNode => {
    const firstPart = formatMessage({
      id: editedAutomation
        ? 'form.createAutomation.summary.descriptionEditAutomation'
        : 'form.createAutomation.summary.descriptionNewAutomation',
    })

    if (requestAutomationType === RequestAutomationType.AI_SCREEN) return null
    if (requestAutomationType === RequestAutomationType.AI_ENRICH) return null
    if (requestAutomationType === RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY) return null
    if (requestAutomationType === RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL) return null

    if (isLoadingAffectedCount) return <Skeleton variant="text" />
    const affectedCompaniesTextId = editedAutomation
      ? 'form.createAutomation.summary.descriptionForProvidersInCategoryEdited'
      : 'form.createAutomation.summary.descriptionForProvidersInCategoryNew'
    const affectedCompaniesText = formatMessage(
      {
        id: affectedCompaniesTextId,
      },
      {
        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
        count: affectedCount ?? 0,
      },
    )
    return (
      <>
        {firstPart} {affectedCompaniesText}
      </>
    )
  }

  const QUESTIONS: AutomationQuestion[] = [
    ...insertIf(!editedAutomation, {
      title: formatMessage({ id: 'form.createAutomation.automationType.header' }),
      description: formatMessage({ id: 'form.createAutomation.automationType.description' }),
      fieldName: AUTOMATION_QUESTION_KEYS.AUTOMATION_TYPE,
      children: <AutomationType />,
      hideNextButton: true,
      disableInitialBack: true,
    }),
    ...(requestAutomationType === RequestAutomationType.AI_SCREEN
      ? [
          {
            title: formatMessage({ id: 'form.scheduleProviderScreening.selectQuestionnaire.title' }),
            children: <SelectTemplateForScraping name={AUTOMATION_QUESTION_KEYS.TEMPLATES} checkExistingAutomations />,
            fieldName: AUTOMATION_QUESTION_KEYS.TEMPLATES,
          },
        ]
      : requestAutomationType === RequestAutomationType.AI_ENRICH
        ? [] // AI Enrich goes directly to summary, which includes the Apply To selection
        : [
            ...insertIf(requestAutomationType === RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY, {
              title: formatMessage({ id: 'form.createAutomation.selectCategories.title' }),
              description: formatMessage({ id: 'form.createAutomation.selectCategories.description' }),
              fieldName: AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS,
              children: <AutomationCategories />,
            }),
            ...insertIf(requestAutomationType === RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL, {
              title: formatMessage({ id: 'form.createAutomation.selectAssessmentLevel.title' }),
              description: formatMessage({ id: 'form.createAutomation.selectAssessmentLevel.description' }),
              fieldName: AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS,
              children: <AutomationAssessments />,
              canGoNext: (fieldValues: unknown) => {
                const formData = fieldValues as AutomationFormData
                const categoryFilters = formData.categoryFilters || []

                const validation = validateAutomationAssessmentFilters(categoryFilters)
                return validation.isValid
              },
            }),
            ...insertIf(requestAutomationType === RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL, {
              title: formatMessage({ id: 'form.createAutomation.selectCategories.title' }),
              description: formatMessage({ id: 'form.createAutomation.selectAssessmentLevel.description' }),
              fieldName: AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS,
              children: <AutomationCategories hideProductCategories />,
              hasSkipButton: true,
            }),
            ...insertIf(!editedAutomation && !selectedQuestionnaireTemplates?.length, {
              title: formatMessage({ id: 'form.createRequest.mainMenu.title' }),
              fieldName: AUTOMATION_QUESTION_KEYS.REPORT_TYPE,
              children: <AutomationQuestionnaireType />,
              hideNextButton: true,
            }),
            {
              title: formatMessage({ id: 'form.createAutomation.selectQuestionnaire.title' }),
              description: formatMessage({
                id:
                  requestAutomationType === RequestAutomationType.FOR_EVERY_NEW_PROVIDER
                    ? 'form.createAutomation.selectQuestionnaire.descriptionForEveryNewProvider'
                    : 'form.createAutomation.selectQuestionnaire.descriptionForProvidersInCategory',
              }),
              fieldName: AUTOMATION_QUESTION_KEYS.TEMPLATES,
              children: <AutomationQuestionnaires selectedQuestionnaireTemplates={selectedQuestionnaireTemplates} />,
            },
            {
              title: formatMessage({ id: 'form.createAutomation.selectPeriod.title' }),
              description: formatMessage({
                id:
                  questionnaireType === QuestionnaireType.COMPANY
                    ? 'form.createAutomation.selectPeriod.descriptionCompanySpecific'
                    : 'form.createAutomation.selectPeriod.descriptionWfStandard',
              }),
              fieldName: AUTOMATION_QUESTION_KEYS.DATE_YEAR,
              children: <AutomationPeriod />,
              canGoNext: (fieldValues: unknown) => {
                const formData = fieldValues as AutomationFormData
                if (formData.dateType === Periods.NoPeriod) return true
                if (formData.dateType === Periods.Yearly) return Boolean(formData.dateYear)
                return Boolean(formData.dateYear) && Boolean(formData.datePeriod)
              },
            },
            {
              title: formatMessage({ id: 'form.createAutomation.deadlineOffset.title' }),
              description: formatMessage({
                id: 'form.createAutomation.deadlineOffset.description',
              }),
              fieldName: [
                AUTOMATION_QUESTION_KEYS.DEADLINE_OFFSET,
                AUTOMATION_QUESTION_KEYS.DEADLINE_OFFSET_TYPE,
                AUTOMATION_QUESTION_KEYS.CUSTOM_DUE_DATE,
              ],
              children: <AutomationDeadlineOffset />,
              hasSkipButton: true,
              canGoNext: (fieldValues: unknown) => {
                const formData = fieldValues as AutomationFormData
                // Check for validation errors on custom date field
                if (formState.errors[AUTOMATION_QUESTION_KEYS.CUSTOM_DUE_DATE]) {
                  return false
                }
                // If custom date is selected, require a date value
                if (formData.deadlineOffsetType === 'custom') {
                  return Boolean(formData.customDueDate)
                }
                // Otherwise, require a preset option
                return Boolean(formData.deadlineOffsetDays || formData.deadlineOffsetType)
              },
              customOnClickSkip: () => {
                setValue(AUTOMATION_QUESTION_KEYS.DEADLINE_OFFSET, undefined)
                setValue(AUTOMATION_QUESTION_KEYS.DEADLINE_OFFSET_TYPE, undefined)
                setValue(AUTOMATION_QUESTION_KEYS.CUSTOM_DUE_DATE, undefined)
                setActiveStep(current => current + 1)
              },
            },
          ]),
    {
      title: formatMessage({ id: 'form.createAutomation.summary.title' }),
      overrideButtonTextId: editedAutomation
        ? 'form.createAutomation.summary.saveAutomation'
        : 'form.createAutomation.summary.finalizeAutomation',
      description: getReviewStepDescription(),
      fieldName: '',
      children: <AutomationSummary />,
      disableInitialBack:
        editedRequestAutomation?.automationType === RequestAutomationType.AI_ENRICH ||
        requestAutomationType === RequestAutomationType.AI_ENRICH,
      // Validation for applyToExisting is done on submit, not via canGoNext
    },
  ]

  useEffect(() => {
    setQueryFilters([])
    if (!open) {
      setActiveStep(1)
      reset({})
    }
  }, [open])

  const onSubmit = async (values: AutomationFormData) => {
    if (requiresApplyToExistingSelection(values.automationType) && values.applyToExisting === undefined) {
      setError(AUTOMATION_QUESTION_KEYS.APPLY_TO_EXISTING, {
        type: 'required',
        message: 'form.createAutomation.applyTo.selectionRequired',
      })
      return
    }

    const { periodEndsAt, periodStartsAt } = calculateStartsAndEndsAtDate({
      dateType: values.dateType,
      datePeriod: values.datePeriod,
      dateYear: values.dateYear,
    })

    // Handle deadline offset: use preset number or custom date
    let deadlineOffsetDays: number | null = null
    let customDueDate: string | null = null

    if (values.deadlineOffsetType === 'custom' && values.customDueDate) {
      // If a custom date was selected, format as YYYY-MM-DD (date-only) to avoid timezone issues
      try {
        const dateObj = values.customDueDate instanceof Date ? values.customDueDate : new Date(values.customDueDate)

        if (isNaN(dateObj.getTime())) {
          throw new Error('Invalid date')
        }

        // Format as YYYY-MM-DD to preserve the calendar date regardless of timezone
        const year = dateObj.getFullYear()
        const month = String(dateObj.getMonth() + 1).padStart(2, '0')
        const day = String(dateObj.getDate()).padStart(2, '0')
        customDueDate = `${year}-${month}-${day}`
        deadlineOffsetDays = null
      } catch (error) {
        setError(AUTOMATION_QUESTION_KEYS.CUSTOM_DUE_DATE, {
          type: 'manual',
          message: formatMessage({ id: 'form.validation.invalidDate' }),
        })
        return
      }
    } else if (typeof values.deadlineOffsetType === 'number') {
      deadlineOffsetDays = values.deadlineOffsetType
      customDueDate = null
    } else if (typeof values.deadlineOffsetDays === 'number') {
      deadlineOffsetDays = values.deadlineOffsetDays
      customDueDate = null
    }

    // Build payload based on automation type requirements
    const body = buildAutomationPayload(values.automationType, {
      id: editedRequestAutomation?.id,
      templateIds: values.templates?.map(template => template.id) ?? [],
      periodStartsAt,
      periodEndsAt,
      deadlineOffsetDays,
      customDueDate,
      categoryFilters: values.categoryFilters,
      applyToExisting: values.applyToExisting ?? false,
    })

    await createAutomation(
      { body: body, url: endpoints.saveAutomation },
      {
        onSuccess: () => {
          onAutomationSaved(values)
        },
      },
    )
  }

  const onAutomationSaved = (values: AutomationFormData) => {
    showSnackbar({
      message: formatMessage({ id: 'notifications.automationCreated' }),
      severity: NotificationSeverity.success,
      disableAutoClose: true,
    })

    onSaved?.()

    if (values.automationType === RequestAutomationType.AI_ENRICH && values.applyToExisting) {
      queryClient.invalidateQueries({ queryKey: [FetchKey.JobRunning, 'enrichment'] })
      window.setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [FetchKey.JobRunning, 'enrichment'] })
      }, 2000)
      window.setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [FetchKey.JobRunning, 'enrichment'] })
      }, 5000)
    }

    if (values.automationType === RequestAutomationType.AI_SCREEN && values.applyToExisting) {
      queryClient.invalidateQueries({ queryKey: [FetchKey.JobRunning, 'screening'] })
      window.setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [FetchKey.JobRunning, 'screening'] })
      }, 2000)
      window.setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [FetchKey.JobRunning, 'screening'] })
      }, 5000)
    }

    onClose()
  }

  const activeQuestion = useMemo(
    () => QUESTIONS[activeStep - 1],
    [activeStep, QUESTIONS.map(q => q.title).join(','), QUESTIONS.length, Boolean(editedAutomation)],
  )

  // On the first step (automation type selection), hide the progress and show simpler title
  const isOnAutomationTypeStep = activeStep === 1 && !editedAutomation

  // Use the same step numbers for display as in the progress context
  const displayedActiveStep = activeStep
  const displayedTotalSteps = QUESTIONS.length

  useEffect(() => {
    setTotalSteps(QUESTIONS.length)
  }, [QUESTIONS.length])

  const modalTitle = isOnAutomationTypeStep
    ? formatMessage({ id: 'form.createAutomation.automationType.title' })
    : formatMessage({
        id: `configurations.${editedAutomation ? 'editAutomation' : 'automation'}`,
      })

  return (
    <Dialog open={open} fullScreen onClose={onClose}>
      <Filters allowedFilters={PROVIDER_SELECTOR_ALLOWED_FILTERS}>
        {({ clearQueryFilter }) => (
          <CreationModalContainer
            title={modalTitle}
            showProgress={!isOnAutomationTypeStep}
            displayedActiveStep={displayedActiveStep}
            displayedTotalSteps={displayedTotalSteps}
            onClose={() => {
              clearQueryFilter()
              onClose()
              setActiveStep(1)
            }}
          >
            <FormProvider {...formMethods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                {activeQuestion && (
                  <CreationModalStep
                    title={activeQuestion.title}
                    description={activeQuestion.description}
                    fieldnames={
                      Array.isArray(activeQuestion.fieldName)
                        ? activeQuestion.fieldName.filter(Boolean)
                        : [activeQuestion.fieldName].filter(Boolean)
                    }
                    onBack={() => undefined}
                    hideNextButton={activeQuestion.hideNextButton}
                    disableInitialBack={activeQuestion.disableInitialBack}
                    hasSkipButton={activeQuestion.hasSkipButton}
                    isLoading={isAutomationLoading}
                    overrideButtonTextId={activeQuestion.overrideButtonTextId}
                    canGoNext={activeQuestion.canGoNext}
                    customOnClickSkip={activeQuestion.customOnClickSkip}
                    footerContent={activeQuestion.footerContent}
                  >
                    {activeQuestion.children}
                  </CreationModalStep>
                )}
              </form>
            </FormProvider>
          </CreationModalContainer>
        )}
      </Filters>
    </Dialog>
  )
}

export default CreateAutomationModal
